import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function InteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Interior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Ventura', 'Thousand Oaks'],
    description: 'Professional mobile interior car detailing services including deep cleaning, shampooing, steam sanitizing, and leather conditioning.'
  };
  const faqs = [{
    question: 'What does interior detailing include?',
    answer: 'Our interior detail includes deep vacuuming, shampooing of carpets and fabric seats, leather cleaning and conditioning, steam sanitizing of vents and high-touch surfaces, plastic and vinyl restoration, and streak-free interior glass.'
  }, {
    question: 'How long does interior detailing take?',
    answer: 'Interior detailing typically takes 2\u20133 hours depending on the size and condition of your vehicle. Heavily soiled interiors or pet hair removal may take longer.'
  }, {
    question: 'Can you remove stains and odors?',
    answer: 'Yes. Our shampooing and steam extraction lift most deep-set stains, and our sanitizing process neutralizes odors from spills, smoke, and pets.'
  }, {
    question: 'How much does interior detailing cost in Ventura County?',
    answer: 'Interior detailing starts at $180, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you come to my home or office?',
    answer: 'Yes — we are fully mobile, bringing our own water and detailing equipment to your home or office anywhere in Ventura County. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Interior Detailing',
    item: 'https://www.fulldetailpro.com/services/interior-detailing'
  }];
  return <ServicePageLayout title="Interior Detailing Ventura County" subtitle="Restore your vehicle's cabin to a factory-fresh feel with our comprehensive mobile interior detailing service. We come directly to your home or office." metaDescription="Mobile interior detailing in Ventura County, including Camarillo, Ventura and Thousand Oaks. Deep cleaning, stain removal and steam sanitizing. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-1002" data-mp="h575qw2o8dhh">
      <p data-id="element-1003" data-mp="3plsfif34naln">
        Your vehicle's interior is where you spend all your time driving. Over
        time, dirt, dust, spills, pet hair, and odors can accumulate, making
        your daily commute less enjoyable and potentially impacting your health.
        At Mike's Pro Detail, our{' '}
        <strong data-id="element-1004" data-mp="1cfvjsoqff5ky">mobile interior car detailing</strong>{' '}
        service is designed to deep clean, sanitize, and restore your cabin to a
        like-new condition. Whether you're in Camarillo CA, Thousand Oaks, or
        anywhere in Ventura County, our fully equipped mobile unit brings
        premium interior detailing directly to your doorstep.
      </p>

      <h2 data-id="element-1005" data-mp="17gr89rg14vrg">What Our Interior Detailing Includes</h2>
      <p data-id="element-1006" data-mp="1v5tqb0eksqmh">
        We don't just wipe down surfaces; we perform a meticulous, multi-step
        deep cleaning process to ensure every crack and crevice is addressed.
        Our comprehensive interior detail includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-1007" data-mp="1lfr017yu9qru">
        <li className="flex items-start" data-id="element-1008" data-mp="1qf3nnuxjb1vh">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1009" data-mp="1qe275axr1n6d" />
          <span data-id="element-1010" data-mp="2e4pjc8ep6yyq">
            <strong data-id="element-1011" data-mp="2e460mph846jn">Thorough Vacuuming:</strong> Deep extraction of dirt,
            debris, and pet hair from carpets, seats, floor mats, and the trunk
            area.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1012" data-mp="v84bhrpx0q18">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1013" data-mp="v5012cluwy24" />
          <span data-id="element-1014" data-mp="q6os043dyrky">
            Cleaning of fabric seats, carpets, and floor mats to lift deep-set
            stains and grime.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1015" data-mp="galoatsvykvw">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1016" data-mp="gbn1p4xdjjgs" />
          <span data-id="element-1017" data-mp="1dy3zeba0rz32">
            <strong data-id="element-1018" data-mp="1dwj42jfvsc45">Leather Cleaning & Conditioning:</strong> Safe, pH-balanced
            cleaning of leather seats followed by a premium conditioner to
            prevent cracking and fading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1019" data-mp="145y5gw07j7l8">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1020" data-mp="z9hkgaob19cd" />
          <span data-id="element-1021" data-mp="z73mnu28pj14">
            <strong data-id="element-1022" data-mp="1wo25ilu9bi42">Steam Cleaning & Sanitization:</strong> High-pressure steam
            is used on vents, cup holders, and high-touch areas to kill bacteria
            and melt away sticky residue.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1023" data-mp="207azar3xgzkh">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1024" data-mp="208m2optgo5b2" />
          <span data-id="element-1025" data-mp="248d03cdhtjok">
            <strong data-id="element-1026" data-mp="249xki963fm8p">Plastics & Vinyl Restoration:</strong> Cleaning and UV
            protection applied to the dashboard, door panels, and center console
            to restore a rich, non-greasy finish.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1027" data-mp="2j5dg4imbnq77">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1028" data-mp="2j4ly6alndqze" />
          <span data-id="element-1029" data-mp="2n5xflsudb5wi">
            <strong data-id="element-1030" data-mp="2n5e8l769a1x9">Streak-Free Windows:</strong> Interior glass and mirrors are
            cleaned to a crystal-clear, streak-free shine for optimal
            visibility.
          </span>
        </li>
      </ul>

      <h2 data-id="element-1031" data-mp="2ww0iqlv7e08k">The Benefits of Professional Interior Detailing</h2>
      <p data-id="element-1032" data-mp="31w4ww58n2ivm">
        Investing in a professional interior detail goes beyond just aesthetics.
        A clean interior significantly improves the air quality inside your
        vehicle by removing dust mites, allergens, and bacteria trapped in the
        upholstery and ventilation system. Furthermore, regular conditioning of
        leather and UV protection on plastics prevents premature aging,
        cracking, and fading caused by the intense Southern California sun. If
        you're planning to sell your vehicle, a pristine interior is one of the
        most effective ways to maximize its resale value.
      </p>

      <h2 data-id="element-1033" data-mp="2zvquuens1lkp">Why Choose Mike's Pro Detail for Interior Detailing?</h2>
      <p data-id="element-1034" data-mp="2uygxmcu5c5mb">
        When searching for "Mobile Auto Detailing Camarillo CA" or "Car
        Detailing Camarillo", you want a service that is reliable, meticulous,
        and convenient. Mike's Pro Detail stands out because we use only
        premium, eco-friendly products and professional-grade equipment steam
        cleaners. We understand that your time is valuable, which is why our
        mobile detailing Ventura County service comes entirely to you—fully
        equipped with our own water and detailing equipment. We require access
        to a standard electrical outlet at the service location. From Camarillo
        and Ventura to Thousand Oaks, Simi Valley, and Westlake Village, we
        deliver 5-star results without you ever having to leave your home or
        office.
      </p>

      <h2 data-id="element-1035" data-mp="3he7lh6651z25">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-1036" data-mp="3hcweyylb6d7c">
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1037" data-mp="33dwtu0x08gfx">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1038" data-mp="2tiwcqmcsnq0z">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1039" data-mp="wngpq81qmwjt">
          Mobile Detailing &rarr;
        </Link>
        <Link to="/services/headlight-restoration" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1040" data-mp="36uras2im34nu">
          Headlight Restoration &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}